import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

export interface ClinicMemberOption {
  user_id: string;
  name: string;
  email: string;
  role: Database['public']['Enums']['clinic_role'];
}

export const clinicMembersQueryKey = (clinicId: string | null) =>
  ['clinic-members', clinicId] as const;

export function useClinicMembers(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: clinicMembersQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ClinicMemberOption[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('clinic_memberships')
        .select('user_id, role, status, user:users!clinic_memberships_user_id_fkey(name, email)')
        .eq('clinic_id', clinicId)
        .eq('status', 'active');

      if (error) throw error;

      type Joined = {
        user_id: string;
        role: Database['public']['Enums']['clinic_role'];
        user: { name: string | null; email: string } | null;
      };

      return ((data ?? []) as unknown as Joined[])
        .map(
          (row): ClinicMemberOption => ({
            user_id: row.user_id,
            role: row.role,
            name: row.user?.name ?? row.user?.email ?? 'Unknown',
            email: row.user?.email ?? '',
          }),
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 5 * 60_000,
  });
}
